import axios from 'axios';

// Use env-configured base URL if provided; otherwise rely on relative /api
// so the server (Nginx) can proxy appropriately in production.
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminUser');
      localStorage.removeItem('mustChangePassword');
      const path = window.location.pathname;
      if (path !== '/login' && path !== '/change-password') {
        window.location.href = '/login';
      }
    }
    if (error.response?.status === 403) {
      try {
        const raw = localStorage.getItem('adminUser');
        const u = raw ? JSON.parse(raw) : null;
        if (u?.role === 'scanner' && window.location.pathname !== '/scanner') {
          window.location.replace('/scanner');
        }
      } catch {
        /* ignore */
      }
    }
    return Promise.reject(error);
  }
);

// Auth (no token required for login/change-password)
export const authService = {
  login: (username, password) =>
    api.post('/auth/login/', { username, password }),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password/', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  getMe: () => api.get('/auth/me/'),
  logout: () => api.post('/auth/logout/').catch(() => {}),
};

export const dashboardService = {
  getStats: () => api.get('/dashboard/stats/'),
  getRecentBookings: () => api.get('/dashboard/recent_bookings/'),
};

export const bookingService = {
  getAll: (params) => api.get('/bookings/', { params }),
  getById: (id) => api.get(`/bookings/${id}/`),
  cancel: (id) => api.post(`/bookings/${id}/cancel/`),
  confirm: (id) => api.post(`/bookings/${id}/confirm/`),
  markVisited: (id) => api.post(`/bookings/${id}/mark_visited/`),
};

export const locationService = {
  getAll: () => api.get('/locations/'),
  getById: (id) => api.get(`/locations/${id}/`),
  create: (data) => api.post('/locations/', data),
  update: (id, data) => api.put(`/locations/${id}/`, data),
  delete: (id) => api.delete(`/locations/${id}/`),
};

export const ticketCategoryService = {
  getAll: (params) => api.get('/ticket-categories/', { params }),
  getById: (id) => api.get(`/ticket-categories/${id}/`),
  create: (data) => api.post('/ticket-categories/', data),
  update: (id, data) => api.put(`/ticket-categories/${id}/`, data),
  delete: (id) => api.delete(`/ticket-categories/${id}/`),
};

const formDataConfig = (data) =>
  data instanceof FormData
    ? {
        transformRequest: [
          (body, headers) => {
            if (body instanceof FormData) {
              delete headers['Content-Type'];
            }
            return body;
          },
        ],
      }
    : {};

export const ticketService = {
  getAll: () => api.get('/tickets/'),
  getById: (id) => api.get(`/tickets/${id}/`),
  /** Use FormData when uploading images (event thumbnail/banner). */
  create: (data) => api.post('/tickets/', data, formDataConfig(data)),
  update: (id, data) => api.put(`/tickets/${id}/`, data, formDataConfig(data)),
  delete: (id) => api.delete(`/tickets/${id}/`),
};

export const ticketPriceService = {
  getAll: (params) => api.get('/ticket-prices/', { params }),
  create: (data) => api.post('/ticket-prices/', data),
  update: (id, data) => api.put(`/ticket-prices/${id}/`, data),
  delete: (id) => api.delete(`/ticket-prices/${id}/`),
};

export const addonService = {
  getAll: () => api.get('/addons/'),
  getById: (id) => api.get(`/addons/${id}/`),
  create: (data) => api.post('/addons/', data),
  update: (id, data) => api.put(`/addons/${id}/`, data),
  delete: (id) => api.delete(`/addons/${id}/`),
};

export const addonPriceService = {
  getAll: (params) => api.get('/addon-prices/', { params }),
  create: (data) => api.post('/addon-prices/', data),
  update: (id, data) => api.put(`/addon-prices/${id}/`, data),
  delete: (id) => api.delete(`/addon-prices/${id}/`),
};

export const promocodeService = {
  getAll: (params) => api.get('/promocodes/', { params }),
  getById: (id) => api.get(`/promocodes/${id}/`),
  create: (data) => api.post('/promocodes/', data),
  update: (id, data) => api.put(`/promocodes/${id}/`, data),
  delete: (id) => api.delete(`/promocodes/${id}/`),
};

export const paymentLogService = {
  getAll: (params) => api.get('/payment-logs/', { params }),
  getById: (id) => api.get(`/payment-logs/${id}/`),
};

export const scannerService = {
  verify: (data) => api.post('/scanner/verify/', data),
  markVisited: (data) => api.post('/scanner/mark-visited/', data),
  markExpired: (data) => api.post('/scanner/mark-expired/', data),
};

export default api;
